/**
 * Helpers for customer WhatsApp updates (quotes, media, MOT / completion notices).
 * Phone URLs must be HTTPS and publicly readable for Meta Cloud API media-by-link.
 */

import { configHelpers, whatsappConfig } from '../config/app';
import { formatMotTimeSlotDisplay, toDateOnly } from './helpers';

/**
 * Normalize UK/international numbers for Meta (avoids +44 + 44… double prefix).
 * @param {string} input - Raw contact or phone from vehicle
 * @returns {string|null} E.164-style string with leading +
 */
export function normalizePhoneForWhatsApp(input) {
  if (!input || typeof input !== 'string') return null;
  const d = input.replace(/\D/g, '');
  if (d.length < 10) return null;
  if (d.startsWith('44') && d.length >= 12 && d.length <= 14) {
    return `+${d}`;
  }
  return configHelpers.formatPhoneNumber(input);
}

/**
 * Build quote / inspection WhatsApp text + optional video media payloads.
 * @param {object} p - Same shape as sendQuoteToCustomer email payload
 * @returns {{ message: string, media: Array<{type:string,link:string,caption?:string}> }}
 */
export function buildQuoteWhatsAppPayload(p) {
  const company = (p.companyName || '').trim() || 'Garage';
  const amountText =
    p.estimateAmount != null && p.estimateAmount !== ''
      ? `£${Number(p.estimateAmount).toFixed(2)}`
      : 'As quoted';
  const lines = [
    `Dear ${(p.customerName || 'Customer').trim() || 'Customer'},`,
    '',
    `Repair quote from ${company}.`,
    '',
    `Registration: ${p.vehicleReg || 'N/A'}`,
    `Vehicle: ${p.vehicleName || 'N/A'}`,
    `Work summary: ${(p.summary || '').trim() || 'N/A'}`,
    `Estimated cost: ${amountText}`,
  ];
  if ((p.estimateDescription || '').trim()) {
    lines.push(`Description: ${(p.estimateDescription || '').trim()}`);
  }
  lines.push('', 'Please contact us to approve this work or if you have any questions.', '', 'Best regards,', company);

  const message = lines.join('\n');
  const media = [];
  const insp = (p.inspectionVideoUrl || '').trim();
  const fin = (p.finalWorkVideoUrl || '').trim();
  if (insp && insp.startsWith('https://')) {
    media.push({ type: 'video', link: insp, caption: 'Inspection video' });
  }
  if (fin && fin.startsWith('https://')) {
    media.push({ type: 'video', link: fin, caption: 'Completed work video' });
  }
  return { message, media };
}

/**
 * Vehicle media folders → WhatsApp image/video list (max 10 items).
 * @param {object} opts
 * @param {string[]} opts.imageUrls
 * @param {string[]} opts.videosBefore
 * @param {string[]} opts.videosAfter
 * @param {string} [opts.intro] - Optional short text before media
 */
export function buildVehicleMediaWhatsAppPayload({
  imageUrls = [],
  videosBefore = [],
  videosAfter = [],
  intro = '',
}) {
  const media = [];
  const pushUrl = (url, type, caption) => {
    const u = String(url || '').trim();
    if (!u.startsWith('https://')) return;
    if (media.length >= 10) return;
    media.push({ type, link: u, caption });
  };
  (imageUrls || []).forEach((u, i) => pushUrl(u, 'image', `Photo ${i + 1}`));
  (videosBefore || []).forEach((u, i) => pushUrl(u, 'video', `Before video ${i + 1}`));
  (videosAfter || []).forEach((u, i) => pushUrl(u, 'video', `After video ${i + 1}`));
  const message =
    (intro && intro.trim()) ||
    (media.length ? 'Vehicle photos and videos from the garage.' : '');
  return { message, media };
}

/**
 * MOT reminder (session message — works if customer messaged you recently; otherwise use templates in Meta).
 */
export function buildMotReminderMessage({ companyName, vehicleReg, vehicleName, motExpiryDate }) {
  const company = (companyName || '').trim() || 'Garage';
  const dateStr = motExpiryDate
    ? new Date(motExpiryDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'soon';
  return [
    `Hello from ${company}.`,
    '',
    `Your vehicle ${vehicleName || 'vehicle'} (${vehicleReg || 'reg TBC'}) has an MOT due ${dateStr}.`,
    '',
    'Reply to this message or call us to book.',
    '',
    company,
  ].join('\n');
}

/**
 * Resolve MOT expiry for WhatsApp from motExpiryDate or motStatus text
 * (e.g. "MOT due 27 Aug 2026", "MOT valid until 4 Jul 2027").
 * @param {{ motExpiryDate?: string, motStatus?: string }} vehicle
 * @returns {string|null} ISO date string or YYYY-MM-DD when parseable
 */
export function resolveMotExpiryDateForWhatsApp(vehicle) {
  if (!vehicle || typeof vehicle !== 'object') return null;
  const raw = vehicle.motExpiryDate;
  if (raw != null && String(raw).trim()) {
    const s = String(raw).trim();
    const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }

  const status = String(vehicle.motStatus || '').trim();
  if (!status) return null;

  const isoInStatus = status.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoInStatus) return isoInStatus[1];

  const enGb = status.match(
    /\b(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})\b/i
  );
  if (!enGb) return null;
  const months = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  const day = parseInt(enGb[1], 10);
  const month = months[enGb[2].slice(0, 3).toLowerCase()];
  const year = parseInt(enGb[3], 10);
  if (month == null || !day || !year) return null;
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Work complete / ready for collection.
 */
export function buildWorkCompleteMessage({ companyName, vehicleReg, vehicleName, extraNote }) {
  const company = (companyName || '').trim() || 'Garage';
  const lines = [
    `Hello from ${company}.`,
    '',
    `Good news — work on your vehicle ${vehicleName || ''} (${vehicleReg || 'reg TBC'}) is complete.`,
  ];
  if ((extraNote || '').trim()) {
    lines.push('', (extraNote || '').trim());
  }
  lines.push('', 'You can collect your vehicle when ready. Reply or call us with any questions.', '', company);
  return lines.join('\n');
}

/**
 * Meta policy: free-form session messages only reach customers inside the customer care window
 * (typically 24 hours after they last messaged your business WhatsApp number). Outside that,
 * use approved message templates or ask the customer to message you first.
 */
export const WHATSAPP_SESSION_DELIVERY_NOTICE =
  'WhatsApp only delivers these messages when the customer has messaged your business number within the last 24 hours. For a new chat or after 24 hours, use an approved template in Meta Business Manager, or ask the customer to message you first.';

/**
 * Success alert including session policy so staff are not misled when the API returns 200 but the customer sees nothing.
 * @param {string} shortSuccessMessage - e.g. "Quote sent on WhatsApp."
 * @param {string} [rawPhone] - Contact string; shows normalized recipient so you can confirm the same number as the customer’s WhatsApp.
 */
export function alertWhatsAppSent(shortSuccessMessage, rawPhone) {
  if (typeof window === 'undefined') return;
  const lines = [shortSuccessMessage];
  if (rawPhone) {
    const n = normalizePhoneForWhatsApp(rawPhone) || configHelpers.formatPhoneNumber(rawPhone);
    if (n) lines.push(`Recipient: ${n}`);
  }
  lines.push(WHATSAPP_SESSION_DELIVERY_NOTICE);
  window.alert(lines.join('\n\n'));
}

/** True when REACT_APP_WHATSAPP_OUTREACH_TEMPLATE_NAME is set (rebuild after changing .env). */
export function isOutreachTemplateConfigured() {
  return Boolean(whatsappConfig.outreachTemplateName);
}

/** True when REACT_APP_SERVICE_DUE_WHATSAPP_TEMPLATE_NAME is set (rebuild after changing .env). */
export function isServiceDueTemplateConfigured() {
  return Boolean(whatsappConfig.serviceDueTemplateName);
}

function formatServiceDueDateLabel(nextServiceDue) {
  const d = toDateOnly(nextServiceDue);
  if (!d) return '';
  const [y, m, dd] = d.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Meta template for next-service-due WhatsApp (manual send from vehicle modal).
 * When param count is 2–5: customer, company, reg, vehicle, due date. Same idea as Cloud Function template.
 */
/**
 * Maps vehicle/customer data to template variables for the service_due_reminder template:
 *   {{1}} customer name, {{2}} company, {{3}} service due date, {{4}} vehicle reg
 * Legacy 5-param templates (old format) also supported via param count config.
 */
export function buildServiceDueTemplateManualPayload({
  customerName,
  companyName,
  vehicleReg,
  vehicleName,
  nextServiceDue,
} = {}) {
  const name = whatsappConfig.serviceDueTemplateName;
  if (!name) return null;
  const languageCode = whatsappConfig.serviceDueTemplateLanguage || 'en';
  const n = whatsappConfig.serviceDueTemplateBodyParamCount ?? 4;
  const c = String(customerName || 'Customer').slice(0, 1024);
  const co = String(companyName || 'Garage').slice(0, 1024);
  const r = String(vehicleReg || 'N/A').slice(0, 1024);
  const v = String(vehicleName || 'Vehicle').slice(0, 1024);
  const due = String(formatServiceDueDateLabel(nextServiceDue) || '').slice(0, 1024);

  const payload = { name, languageCode };

  if (n <= 0) {
    return payload;
  }

  let parameters;
  if (n === 1) {
    parameters = [{ type: 'text', text: `${c} — ${co} — ${r} — ${due}`.slice(0, 1024) }];
  } else if (n === 4) {
    // service_due_reminder: Hello {{1}}, your {{2}} is due on {{3}}, contact {{4}}
    parameters = [
      { type: 'text', text: c },   // {{1}} customer name
      { type: 'text', text: r },   // {{2}} vehicle reg
      { type: 'text', text: due }, // {{3}} service due date
      { type: 'text', text: co },  // {{4}} company name
    ];
  } else {
    // Legacy 5-param format: customer, company, reg, vehicle, due date
    const parts = [
      { type: 'text', text: c },
      { type: 'text', text: co },
      { type: 'text', text: r },
      { type: 'text', text: v },
      { type: 'text', text: due },
    ];
    parameters = parts.slice(0, Math.min(n, 6));
  }

  payload.components = [{ type: 'body', parameters }];
  return payload;
}

/**
 * Build Meta Cloud API template payload for business-initiated messages.
 * Template body in Business Manager must use {{1}}… in this order when param count is 2–4:
 * 1 customer name, 2 company, 3 registration, 4 vehicle description.
 * For a single variable, use BODY_PARAMS=1 and one {{1}} in the template body.
 */
export function buildOutreachTemplatePayload({
  customerName,
  companyName,
  vehicleReg,
  vehicleName,
} = {}) {
  const name = whatsappConfig.outreachTemplateName;
  if (!name) return null;
  const languageCode = whatsappConfig.outreachTemplateLanguage || 'en';
  const n = whatsappConfig.outreachTemplateBodyParamCount ?? 4;
  const c = String(customerName || 'Customer').slice(0, 1024);
  const co = String(companyName || 'Garage').slice(0, 1024);
  const r = String(vehicleReg || 'N/A').slice(0, 1024);
  const v = String(vehicleName || 'Vehicle').slice(0, 1024);

  const payload = { name, languageCode };

  if (n <= 0) {
    return payload;
  }

  let parameters;
  if (n === 1) {
    parameters = [{ type: 'text', text: `${c} — ${co} — ${r} — ${v}`.slice(0, 1024) }];
  } else {
    const parts = [
      { type: 'text', text: c },
      { type: 'text', text: co },
      { type: 'text', text: r },
      { type: 'text', text: v },
    ];
    parameters = parts.slice(0, Math.min(n, 4));
  }

  payload.components = [{ type: 'body', parameters }];
  return payload;
}

/** True when REACT_APP_WHATSAPP_QUOTE_TEMPLATE_NAME is set. */
export function isQuoteTemplateConfigured() {
  return Boolean(whatsappConfig.quoteTemplateName);
}

/** True when REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_NAME is set. */
export function isMotReminderTemplateConfigured() {
  return Boolean(whatsappConfig.motReminderTemplateName);
}

/** True when REACT_APP_MOT_BOOKED_WHATSAPP_TEMPLATE_NAME is set. */
export function isMotBookedConfirmationTemplateConfigured() {
  return Boolean(whatsappConfig.motBookedConfirmationTemplateName);
}

/**
 * Meta template body params cannot be empty or contain newlines/tabs.
 * @param {string} value
 * @param {string} fallback
 * @returns {string}
 */
function sanitizeWhatsAppTemplateParam(value, fallback) {
  const cleaned = String(value || '')
    .replace(/[\n\r\t]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .slice(0, 1024);
  return cleaned || String(fallback || 'N/A').slice(0, 1024);
}

/**
 * Build Meta Cloud API template payload for MOT booked-in confirmations.
 * Approved mot_booked_confirmation body (5 params):
 *   {{1}} customer name, {{2}} make+model, {{3}} reg, {{4}} date/time, {{5}} company
 */
export function buildMotBookedConfirmationTemplatePayload({
  customerName,
  companyName,
  vehicleReg,
  vehicleName,
  timeSlot,
  dropOffDate,
} = {}) {
  const name = whatsappConfig.motBookedConfirmationTemplateName;
  if (!name) return null;
  const languageCode = whatsappConfig.motBookedConfirmationTemplateLanguage || 'en';
  const c = sanitizeWhatsAppTemplateParam(customerName, 'Customer');
  const co = sanitizeWhatsAppTemplateParam(companyName, 'Garage');
  const r = sanitizeWhatsAppTemplateParam(vehicleReg, 'N/A');
  const v = sanitizeWhatsAppTemplateParam(vehicleName, 'vehicle');
  const booked = sanitizeWhatsAppTemplateParam(
    formatMotTimeSlotDisplay(timeSlot, dropOffDate),
    'the booked time'
  );

  const payload = { name, languageCode };
  payload.components = [{
    type: 'body',
    parameters: [
      { type: 'text', text: c },
      { type: 'text', text: v },
      { type: 'text', text: r },
      { type: 'text', text: booked },
      { type: 'text', text: co },
    ],
  }];
  return payload;
}

/**
 * Build Meta Cloud API template payload for MOT due reminders.
 * Template body variables in Meta Business Manager must follow this order:
 *   {{1}} customer name, {{2}} vehicle reg, {{3}} MOT expiry date, {{4}} company name
 * Use REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_BODY_PARAMS to control how many are used (0–4).
 */
export function buildMotReminderTemplatePayload({
  customerName,
  companyName,
  vehicleReg,
  vehicleName,
  motExpiryDate,
} = {}) {
  const name = whatsappConfig.motReminderTemplateName;
  if (!name) return null;
  const languageCode = whatsappConfig.motReminderTemplateLanguage || 'en';
  const n = whatsappConfig.motReminderTemplateBodyParamCount ?? 4;
  const c = String(customerName || 'Customer').slice(0, 1024);
  const co = String(companyName || 'Garage').slice(0, 1024);
  const r = String(vehicleReg || 'N/A').slice(0, 1024);
  const dateStr = motExpiryDate
    ? new Date(motExpiryDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'soon';
  const d = String(dateStr).slice(0, 1024);

  const payload = { name, languageCode };
  if (n <= 0) return payload;

  // {{1}} customer, {{2}} reg, {{3}} expiry date, {{4}} company
  const parts = [
    { type: 'text', text: c },
    { type: 'text', text: r },
    { type: 'text', text: d },
    { type: 'text', text: co },
  ];
  payload.components = [{ type: 'body', parameters: parts.slice(0, Math.min(n, 4)) }];
  return payload;
}

/**
 * Build Meta template payload for repair quotes.
 * Template body variables (once added in Meta):
 *   {{1}} reg, {{2}} vehicle, {{3}} summary, {{4}} cost, {{5}} description, {{6}} company
 * If body param count is 0 the template is sent with no parameters (static).
 */
export function buildQuoteTemplatePayload({
  vehicleReg,
  vehicleName,
  summary,
  estimateAmount,
  estimateDescription,
  companyName,
} = {}) {
  const name = whatsappConfig.quoteTemplateName;
  if (!name) return null;
  const languageCode = whatsappConfig.quoteTemplateLanguage || 'en';
  const n = whatsappConfig.quoteTemplateBodyParamCount ?? 6;

  const amountText =
    estimateAmount != null && estimateAmount !== ''
      ? `£${Number(estimateAmount).toFixed(2)}`
      : 'As quoted';

  const payload = { name, languageCode };
  if (n <= 0) return payload;

  const parts = [
    { type: 'text', text: String(vehicleReg || 'N/A').slice(0, 1024) },
    { type: 'text', text: String(vehicleName || 'Vehicle').slice(0, 1024) },
    { type: 'text', text: String(summary || 'N/A').slice(0, 1024) },
    { type: 'text', text: amountText.slice(0, 1024) },
    { type: 'text', text: String(estimateDescription || 'N/A').slice(0, 1024) },
    { type: 'text', text: String(companyName || 'Garage').slice(0, 1024) },
  ];
  payload.components = [{ type: 'body', parameters: parts.slice(0, Math.min(n, 6)) }];
  return payload;
}

/** After a successful template send (no session notice — templates are for cold outreach). */
export function alertWhatsAppTemplateSent(rawPhone) {
  if (typeof window === 'undefined') return;
  const lines = [
    'Template message sent via WhatsApp.',
    'This uses your Meta-approved template, so it can reach customers who have not messaged your business number yet.',
  ];
  if (rawPhone) {
    const n = normalizePhoneForWhatsApp(rawPhone) || configHelpers.formatPhoneNumber(rawPhone);
    if (n) lines.push(`Recipient: ${n}`);
  }
  window.alert(lines.join('\n\n'));
}
